import { API_BASE_URL } from "../../config";
import { Mod, ModState } from "./types/mod";
import { getAuthHeaders } from "../Auth/authService";
import { modStore } from "./modStore";
import { collectionStore } from "../Collection/collectionStore";

let listAbortController: AbortController | null = null;

const abortList = (): void =>
{
	if (listAbortController)
	{
		listAbortController.abort();
		listAbortController = null;
	}
};

const list = async (): Promise<Mod[]> =>
{
	abortList();
	listAbortController = new AbortController();

	const response = await fetch(`${API_BASE_URL}/mods`, {
		method: "GET",
		headers: getAuthHeaders(),
		signal: listAbortController.signal
	});

	if (!response.ok)
	{
		throw new Error(response.statusText);
	}

	const result = await response.json();

	const mods = result.map((mod: Mod) => ({
		id: mod.id,
		state: mod.state as ModState,
		reportedSize: mod.reportedSize,
		downloadProgress: mod.downloadProgress,
		sizeOnDisk: mod.sizeOnDisk,
		image: mod.image,
		title: mod.title,
		updateDate: mod.updateDate ? new Date(mod.updateDate) : undefined,
		workshopUpdateDate: new Date(mod.workshopUpdateDate),
		tags: mod.tags,
		scope: mod.scope,
		collections: mod.collections
	}));

	listAbortController = null;
	modStore.set(mods);
	return mods;
};

const update = async (mod: Mod): Promise<void> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/mods/${mod.id}`, {
		method: "PUT",
		headers: getAuthHeaders(),
		body: JSON.stringify(mod)
	});

	if (!response.ok)
	{
		throw new Error("Failed to update mod");
	}

	modStore.update(mod);
	collectionStore.updateMod(mod);
};

const select = async (mods: Mod | Mod[]): Promise<void> =>
{
	const modsArray = Array.isArray(mods) ? mods : [mods];
	const updates = modsArray.map(mod => ({
		...mod,
		state: ModState.Selected
	}));
	await Promise.all(updates.map(update));
};

const unselect = async (mods: Mod | Mod[]): Promise<void> =>
{
	const modsArray = Array.isArray(mods) ? mods : [mods];
	const updates = modsArray.map(mod => ({
		...mod,
		state: ModState.Downloaded
	}));
	await Promise.all(updates.map(update));
};

const selectOnly = async (mods: Mod | Mod[]): Promise<void> =>
{
	const modsArray = Array.isArray(mods) ? mods : [mods];
	const targetIds = new Set(modsArray.map(m => m.id));
	const allMods = modStore.get();

	const modsToUnselect = allMods.filter(mod => mod.state === ModState.Selected && !targetIds.has(mod.id));
	const modsToSelect = modsArray.filter(mod => mod.state !== ModState.Selected && mod.state !== ModState.Collection);

	const updates: Mod[] = [
		...modsToUnselect.map(mod => ({ ...mod, state: ModState.Downloaded as ModState })),
		...modsToSelect.map(mod => ({ ...mod, state: ModState.Selected as ModState }))
	];

	await Promise.all(updates.map(update));
};

const remove = async (id: string): Promise<void> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/mods/${id}`, {
		method: "DELETE",
		headers: {
			"Authorization": sessionStorage.getItem("loginToken") || ""
		}
	});

	if (!response.ok)
	{
		throw new Error("Failed to delete mod");
	}

	const existsInCollection = collectionStore.hasModInCollection(id);
	if (existsInCollection)
	{
		const updatedMod = {
			id,
			state: ModState.Collection,
			sizeOnDisk: undefined,
			updateDate: undefined,
			downloadProgress: undefined,
			isSigned: undefined,
			scope: undefined
		};
		modStore.update(updatedMod);
		collectionStore.updateMod(updatedMod);
	}
	else
	{
		modStore.remove(id);
	}
};

export const modService = {
	list,
	update,
	select,
	selectOnly,
	unselect,
	remove,
};